<template>
  <zm-dialog
    class="opc-healthcare-agreement-dialog"
    :visible.sync="innerVisible"
    top="15vh"
    :append-to-body="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
  >
    <div v-loading="loading" class="opc-healthcare-agreement-dialog__iframe-wrap">
      <iframe
        class="opc-healthcare-agreement-dialog__iframe"
        :src="agreementUrl"
        @load="onIframeLoad"
        @error="onIframeError"
      />
    </div>

    <template #footer>
      <div class="opc-healthcare-agreement-dialog__footer">
        <span v-if="!hasRead" class="opc-healthcare-agreement-dialog__footer__guide-to-accept-tip">
          {{ $t('billing.healthcare.agreement_dialog.guide_to_accept_tip') }}
        </span>
        <span class="opc-healthcare-agreement-dialog__footer__buttons">
          <zm-button type="primary" round :disabled="!hasRead" @click="handleAction('accept')">
            {{ $t('billing.healthcare.agreement_dialog.accept') }}
          </zm-button>
          <zm-button round @click="handleAction('cancel')">{{ $t('billing.common.cancel') }}</zm-button>
        </span>
      </div>
    </template>
  </zm-dialog>
</template>

<script>
import { translateToEn } from '@/plugins/i18n'
import { BUSINESS_COUNTRY_VALUE_MAP, getAgreementUrlByBusinessCountry } from './helpers'
import { ZOOM_MEETING_SUB_TYPES, getTitleNameI18nKey } from '../../common/utils'
import { INDUSTRIES } from '@/common/constants'

function getTrackingAgreementName(businessCountry) {
  switch (businessCountry) {
    case BUSINESS_COUNTRY_VALUE_MAP.UnitedStates:
      return 'BAA'
    case BUSINESS_COUNTRY_VALUE_MAP.Canada:
      return 'PHIA'
    default:
      return ''
  }
}

export default {
  props: {
    visible: Boolean,
    accepted: {
      type: Boolean,
      required: true,
    },
    businessCountry: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      agreementUrl: '',
      hasRead: false,
      trackingData: {
        hostPlanName: translateToEn(getTitleNameI18nKey(ZOOM_MEETING_SUB_TYPES.PRO)),
        planName: translateToEn('billing.healthcare.title'),
      },
    }
  },

  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(newVal) {
        this.$emit('update:visible', newVal)
      },
    },
  },

  watch: {
    innerVisible: {
      immediate: true,
      handler(visible) {
        if (visible) {
          const agreementUrl = getAgreementUrlByBusinessCountry(this.businessCountry)
          if (this.agreementUrl !== agreementUrl) {
            this.loading = true
            this.agreementUrl = agreementUrl
            this.hasRead = this.accepted
          }

          const agreementName = getTrackingAgreementName(this.businessCountry)
          this.$tracking.buyInteraction(
            `${this.trackingData.planName} details`,
            `${this.trackingData.hostPlanName} ${INDUSTRIES.HEALTHCARE} - ${agreementName} agreement modal - impression`,
          )
        }
      },
    },
  },

  mounted() {
    /**
     * Manually append the popup content to `document.body`.
     *
     * Configuring `append-to-body` through `zm-dialog` will move the element to the `document.body` each time it becomes visible, causing the iframe to reload.
     * If the dialog is not appended to the `document.body`, the popup content and overlay may have z-index issues.
     */
    document.body.appendChild(this.$el)
  },

  destroyed() {
    /**
     * Manually remove the popup content from `document.body`.
     */
    if (this.$el?.parentElement) {
      this.$el.parentElement.removeChild(this.$el)
    }
  },

  methods: {
    onIframeLoad(event) {
      this.loading = false

      const iframe = event.target
      if (!this.hasRead && iframe && iframe.contentWindow && iframe.contentDocument) {
        const onScroll = () => {
          if (!this.innerVisible) return
          const doc = iframe.contentDocument
          const scrollHeightThreshold = (doc.documentElement.scrollHeight - doc.documentElement.clientHeight) * 0.8
          if (iframe.contentWindow.scrollY >= scrollHeightThreshold) {
            this.hasRead = true
            doc.removeEventListener('scroll', onScroll)
          }
        }
        iframe.contentDocument.addEventListener('scroll', onScroll)
      }
    },
    onIframeError() {
      this.loading = false
    },
    handleAction(action) {
      if (action === 'accept') {
        this.$emit('update:accepted', true)
      } else if (action === 'cancel') {
        this.$emit('update:accepted', false)
      }
      this.innerVisible = false

      const agreementName = getTrackingAgreementName(this.businessCountry)
      this.$tracking.buyInteraction(
        `${this.trackingData.planName} details`,
        `${this.trackingData.hostPlanName} ${INDUSTRIES.HEALTHCARE} - ${agreementName} agreement modal - ${action}`,
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-healthcare-agreement-dialog {
  ::v-deep {
    .zm-dialog {
      margin: 15vh auto 0;
      padding: 32px 32px 0;
      width: 85%;
      max-width: 1024px;
      height: calc(100% - 30vh);
      display: flex;
      flex-direction: column;
      align-items: stretch;
      @include media(mobile, tablet) {
        margin: 6vh auto 0 !important;
        padding: 24px 24px 0 !important;
        height: calc(100% - 12vh) !important;
      }
      &__header {
        display: none;
      }
      &__body {
        width: 100%;
        flex-grow: 1;
      }
      &__footer {
        width: 100%;
        flex-shrink: 0;
        padding: 0;
      }
    }
  }
  &__iframe {
    width: 100%;
    height: 100%;
    border: 1px solid #cbcbd4;
    border-radius: 4px;
    &-wrap {
      width: 100%;
      height: 100%;
    }
  }
  &__footer {
    padding: 24px 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    &__guide-to-accept-tip {
      padding: 8px 0;
      line-height: 1.2;
      font-size: 14px;
      color: #666487;
    }
    &__buttons {
      margin-left: 40px;
      .zm-button + .zm-button {
        margin-left: 16px;
      }
    }
  }
}
</style>
